import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { Button } from "@shared/components/Button";
import { Pagination } from "@shared/components/Pagination";
import { Select } from "@shared/components/Select";
import { StatusBadge } from "@shared/components/StatusBadge";
import { TextField } from "@shared/components/TextField";
import { useAsync } from "@shared/hooks/useAsync";
import { listArticles } from "./articles.api";
import { KIND_LABEL, type ArticleKind, type ArticleListFilter, type ArticleStatus } from "./types";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "", label: "Semua" },
  { value: "draft", label: "Draf" },
  { value: "published", label: "Terbit" },
  { value: "archived", label: "Diarsipkan" },
];
const KIND_OPTIONS = [
  { value: "", label: "Semua" },
  { value: "article", label: "Artikel" },
  { value: "tip", label: "Tip" },
];

/** The URL is the source of truth for filters; anything unrecognised is dropped, never forwarded. */
function readFilter(params: URLSearchParams): ArticleListFilter {
  const status = params.get("status");
  const kind = params.get("kind");
  const page = Number(params.get("page"));
  return {
    status: status === "draft" || status === "published" || status === "archived" ? status : undefined,
    kind: kind === "tip" || kind === "article" ? kind : undefined,
    q: params.get("q")?.trim().slice(0, 100) || undefined,
    placeholder: params.get("placeholder") === "true" ? true : undefined,
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    limit: PAGE_SIZE,
  };
}

export function ArticleListPage() {
  const [params, setParams] = useSearchParams();
  const filter = readFilter(params);
  const [search, setSearch] = useState(filter.q ?? "");

  const { data, error, loading, reload } = useAsync(
    () => listArticles(filter),
    [filter.status, filter.kind, filter.q, filter.placeholder, filter.page],
  );

  function change(patch: Partial<Record<"status" | "kind" | "q" | "placeholder" | "page", string>>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in patch)) next.delete("page"); // any filter change starts again from page 1
    setParams(next);
  }

  function onSearch(event: FormEvent) {
    event.preventDefault();
    change({ q: search.trim() });
  }

  return (
    <section>
      <header className="page-head">
        <h2>Artikel</h2>
        <Link to="/articles/new" className="btn btn-primary">
          Artikel Baru
        </Link>
      </header>

      <div className="filters">
        <Select
          label="Status"
          options={STATUS_OPTIONS}
          value={filter.status ?? ""}
          onChange={(e) => change({ status: e.target.value as ArticleStatus | "" })}
        />
        <Select
          label="Jenis"
          options={KIND_OPTIONS}
          value={filter.kind ?? ""}
          onChange={(e) => change({ kind: e.target.value as ArticleKind | "" })}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={filter.placeholder === true}
            onChange={(e) => change({ placeholder: e.target.checked ? "true" : "" })}
          />
          Hanya placeholder
        </label>
        <form className="search" onSubmit={onSearch} role="search">
          <TextField label="Cari judul" value={search} onChange={(e) => setSearch(e.target.value)} maxLength={100} />
          <Button type="submit" variant="secondary">
            Cari
          </Button>
        </form>
      </div>

      {error && (
        <div role="alert" className="form-error">
          <p>{error}</p>
          <Button variant="secondary" onClick={reload}>
            Coba lagi
          </Button>
        </div>
      )}
      {loading && !data && <p role="status">Memuat…</p>}

      {data && !error && (
        <>
          {data.data.length === 0 ? (
            <p className="muted">Belum ada artikel.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Judul</th>
                  <th>Jenis</th>
                  <th>Status</th>
                  <th>Diperbarui</th>
                  <th>Oleh</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/articles/${a.id}`}>{a.title}</Link>
                      {a.isPlaceholder && <span className="badge badge-placeholder">Placeholder</span>}
                    </td>
                    <td>{KIND_LABEL[a.kind]}</td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                    <td>{format(new Date(a.updatedAt), "d MMM yyyy HH:mm", { locale: idLocale })}</td>
                    <td>{a.updatedByUsername ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            page={data.pagination.page}
            totalPages={data.pagination.totalPages}
            total={data.pagination.total}
            onPage={(page) => change({ page: String(page) })}
          />
        </>
      )}
    </section>
  );
}
